/**
 * Global Drawer Stack Provider
 * 
 * Provides a global drawer stack management system that can be used across
 * the entire application. This ensures proper z-index stacking, focus management,
 * and escape key handling for all drawers.
 * 
 * Features:
 * - Centralized drawer management with Mantine's native compound components
 * - Swipe-down gesture support for bottom drawers
 * - Consistent close button functionality
 * - Proper z-index stacking and focus management
 * - Type-safe drawer IDs and configurations
 */

import * as React from "react";
import { Drawer } from "@mantine/core";
import { useDrawerState } from "./hooks/useDrawerState";
import { useDrawerConfig } from "./hooks/useDrawerConfig";
import { GlobalDrawerStackContext } from "./context";
import type { GlobalDrawerStackContextType, GlobalDrawerId, DrawerConfig } from "./types";

interface GlobalDrawerStackProviderProps {
  children: React.ReactNode;
}

/**
 * Inner provider component that uses the drawer stack
 * This must be inside the Drawer.Stack context
 */
const GlobalDrawerStackProviderInner: React.FC<GlobalDrawerStackProviderProps> = ({
  children,
}) => {
  const drawerState = useDrawerState();
  const drawerConfig = useDrawerConfig();

  const openDrawer = React.useCallback(
    (id: GlobalDrawerId, config?: Partial<DrawerConfig>) => {
      if (config) {
        drawerConfig.updateDrawerConfig(id, config);
      }
      drawerState.openDrawer(id);
    },
    [drawerState, drawerConfig]
  );

  const closeDrawer = React.useCallback(
    (id: GlobalDrawerId) => {
      drawerState.closeDrawer(id);
    },
    [drawerState]
  );

  const closeAllDrawers = React.useCallback(() => {
    drawerState.closeAllDrawers();
  }, [drawerState]);

  const isDrawerOpen = React.useCallback(
    (id: GlobalDrawerId) => {
      return drawerState.isDrawerOpen(id);
    },
    [drawerState]
  );

  const getDrawerConfigValue = React.useCallback(
    (id: GlobalDrawerId) => {
      return drawerConfig.getDrawerConfig(id);
    },
    [drawerConfig]
  );

  const setDrawerConfigValue = React.useCallback(
    (id: GlobalDrawerId, config: Partial<DrawerConfig>) => {
      drawerConfig.setDrawerConfig(id, config);
    },
    [drawerConfig]
  );

  const contextValue = React.useMemo<GlobalDrawerStackContextType>(
    () => ({
      drawerStack: drawerState.drawerStack,
      openDrawer,
      closeDrawer,
      closeAllDrawers,
      isDrawerOpen,
      getDrawerConfig: getDrawerConfigValue,
      setDrawerConfig: setDrawerConfigValue,
    }),
    [
      drawerState.drawerStack,
      openDrawer,
      closeDrawer,
      closeAllDrawers,
      isDrawerOpen,
      getDrawerConfigValue,
      setDrawerConfigValue,
    ]
  );

  return (
    <GlobalDrawerStackContext.Provider value={contextValue}>
      {children}
    </GlobalDrawerStackContext.Provider>
  );
};

export const GlobalDrawerStackProvider: React.FC<GlobalDrawerStackProviderProps> = ({
  children,
}) => {
  return (
    <Drawer.Stack>
      <GlobalDrawerStackProviderInner>
        {children}
      </GlobalDrawerStackProviderInner>
    </Drawer.Stack>
  );
};
